import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { simulationApi } from '@/events/simulationEngine';
import {
  DEMO_CATALOGUE,
  GROUP_META,
  GROUP_ORDER,
  type DemoCard,
  type DemoGroup,
} from '@/data/demoCatalogue';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { Search, Play, ArrowRight, Sparkles, RotateCw } from 'lucide-react';
import clsx from 'clsx';

/**
 * Facilitator's demo console. A live catalogue of one-click demonstrations
 * grouped by workflow. Each card fires a real sim event (so the rest of the
 * prototype reacts as if the real thing just happened) AND/OR navigates the
 * facilitator to the surface where the demo plays out.
 *
 * This is the primary surface for workshops. The chronological "Next event"
 * timeline still lives in the top bar for anyone who wants the Monday-morning
 * narrative play-through.
 */
export default function PlaytestConsole() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState<DemoGroup | 'all'>('all');
  const narrationOn = useUIStore((s) => s.narrationOn);
  const setNarration = useUIStore((s) => s.setNarration);
  const [resetting, setResetting] = useState(false);

  // Reset Data — restore the seeded baseline, discarding accumulated test edits.
  // The clock is frozen (persistence fast-unlock), so this is the safety net that
  // makes persistent testing usable: poke the platform freely, reset for a clean
  // slate. Hits the dev-gated POST /_dev/reset, which truncates + reseeds the
  // real tables server-side (tv-1vh.14). That path emits a single `seed.loaded`
  // outbox event — NOT the ~1200-write DELETE-then-PUT storm a client-side store
  // empty+refill would fan out over /ws. On success we reload to re-hydrate the
  // clean baseline from /api/bootstrap (a fresh page baselines the persistence
  // bridge, so it fires no writes). On failure we surface it and DON'T reload —
  // a reload would silently mask the failure by re-showing the unchanged data.
  const resetData = async () => {
    if (!window.confirm('Reset all demo data to the seeded baseline? This discards your test edits.')) return;
    setResetting(true);
    try {
      const res = await fetch('/_dev/reset', { method: 'POST' });
      if (!res.ok) throw new Error(`reset failed: ${res.status}`);
      // The seeded baseline is the seed day: put the shared sim clock back to
      // DEMO_NOW too (every open tab follows), so the reload boots on Mon 04 May.
      useSimulationStore.getState().resetClock();
      window.location.reload();
    } catch (err) {
      console.error('Reset Data failed', err);
      window.alert('Reset failed — the demo data was not changed. See the console for details.');
      setResetting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return DEMO_CATALOGUE.filter((card) => {
      if (activeGroup !== 'all' && card.group !== activeGroup) return false;
      if (!q) return true;
      return (
        card.title.toLowerCase().includes(q) || card.subtitle.toLowerCase().includes(q)
      );
    });
  }, [query, activeGroup]);

  const handleFire = (card: DemoCard) => {
    if (card.trigger.kind === 'sim-event') {
      simulationApi.triggerEventByType(card.trigger.eventType);
    }
    if (card.gotoPath) {
      navigate(card.gotoPath, card.gotoState ? { state: card.gotoState } : undefined);
    }
  };

  const counts = useMemo(() => {
    const out: Record<DemoGroup, number> = {
      inbound: 0,
      floor: 0,
      'money-signals': 0,
      'leaderboard-reporting': 0,
    };
    for (const c of DEMO_CATALOGUE) out[c.group] += 1;
    return out;
  }, []);

  return (
    <section className="px-8 py-8 max-w-6xl" data-testid="playtest-console">
      <header className="mb-6">
        <p className="label-small-caps text-gold-bright">Facilitator console</p>
        <h1 className="heading-display text-3xl text-cream mt-2">Demo console</h1>
        <p className="font-serif text-cream-soft mt-2 max-w-3xl">
          One-click demonstrations. Click any card — the prototype reacts as if the real thing
          just happened and lands you on the surface to watch it play out. Use the chronological{' '}
          <span className="font-inter">Next event</span> button in the top bar if you&apos;d rather
          walk Monday morning in order.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="playtest-controls">
        <div className="relative flex-1 min-w-[260px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-cream-faint"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search demos — &quot;call&quot;, &quot;rebook&quot;, &quot;deposit&quot;…"
            className="w-full bg-ink-deep border border-gold-dim/40 rounded-md pl-9 pr-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
            data-testid="playtest-search"
          />
        </div>
        <button
          type="button"
          onClick={resetData}
          disabled={resetting}
          className="btn-quiet text-xs disabled:opacity-50"
          data-testid="playtest-reset"
          title="Restore the seeded baseline, discarding accumulated test edits"
        >
          <RotateCw size={12} /> {resetting ? 'Resetting…' : 'Reset Data'}
        </button>
        <label
          className="flex items-center gap-2 text-xs text-cream-soft cursor-pointer"
          data-testid="playtest-narration-toggle"
        >
          <input
            type="checkbox"
            checked={narrationOn}
            onChange={(e) => setNarration(e.target.checked)}
            className="accent-gold-bright"
          />
          <Sparkles size={12} className="text-gold-bright" />
          Narration overlay
        </label>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="playtest-group-filter">
        <GroupChip
          active={activeGroup === 'all'}
          label="All"
          count={DEMO_CATALOGUE.length}
          onClick={() => setActiveGroup('all')}
          testid="playtest-group-all"
        />
        {GROUP_ORDER.map((g) => (
          <GroupChip
            key={g}
            active={activeGroup === g}
            label={GROUP_META[g].label}
            count={counts[g]}
            onClick={() => setActiveGroup(g)}
            testid={`playtest-group-${g}`}
          />
        ))}
      </div>

      {GROUP_ORDER.filter(
        (g) => activeGroup === 'all' || activeGroup === g,
      ).map((g) => {
        const cards = filtered.filter((c) => c.group === g);
        if (cards.length === 0) return null;
        return (
          <section key={g} className="mb-8" data-testid={`playtest-section-${g}`}>
            <header className="mb-3">
              <h2 className="font-cinzel text-lg text-cream">{GROUP_META[g].label}</h2>
              <p className="font-serif text-cream-soft text-sm mt-0.5">
                {GROUP_META[g].story}
              </p>
            </header>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {cards.map((card) => (
                <li key={card.id}>
                  <button
                    type="button"
                    onClick={() => handleFire(card)}
                    data-testid={`playtest-card-${card.id}`}
                    className="w-full text-left panel hover:border-gold-bright/60 transition p-4 h-full flex flex-col gap-2"
                  >
                    <span className="flex items-start gap-3">
                      <span className="text-2xl shrink-0" aria-hidden="true">
                        {card.emoji}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-inter text-base text-cream leading-snug">
                          {card.title}
                        </span>
                        <span className="block font-serif text-sm text-cream-soft mt-1 leading-snug">
                          {card.subtitle}
                        </span>
                      </span>
                    </span>
                    <span className="mt-auto pt-2 flex items-center justify-between text-[10px] uppercase tracking-widest font-inter text-gold-bright">
                      <span className="flex items-center gap-1">
                        {card.trigger.kind === 'sim-event' ? (
                          <>
                            <Play size={10} /> Fire + go
                          </>
                        ) : (
                          <>
                            <ArrowRight size={10} /> Go
                          </>
                        )}
                      </span>
                      {card.gotoPath ? (
                        <span className="text-cream-faint normal-case tracking-normal font-mono">
                          {card.gotoPath}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {filtered.length === 0 ? (
        <p
          data-testid="playtest-empty"
          className="font-serif text-cream-soft text-center py-8"
        >
          No demos match &quot;{query}&quot;. Try a different word.
        </p>
      ) : null}
    </section>
  );
}

function GroupChip({
  active,
  label,
  count,
  onClick,
  testid,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        'rounded-md border px-3 py-1.5 font-inter text-[11px] uppercase tracking-widest transition',
        active
          ? 'border-gold-bright bg-gold/10 text-gold-bright'
          : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
      )}
    >
      {label}{' '}
      <span className={clsx('ml-1 normal-case', active ? 'text-gold' : 'text-cream-faint')}>
        {count}
      </span>
    </button>
  );
}
